import { useCallback, useState } from 'react';
import type { ListMyServersResponse, ServerView } from '@hearth/shared';
import type { Api } from '../api';
import { linkTo } from '../router';
import { Campfire } from '../ui/Campfire';
import { ServerCard } from './ServerCard';
import { useRefresh, useWakeSleep } from './serverHooks';
import { errorText, pollMs, summaryText } from './serverWords';

/**
 * /app: your servers and your friends'. Refreshes every 5 s while one is waking or falling asleep,
 * every 30 s otherwise, and not at all while the tab is hidden (at once when it's back).
 */
export function Servers({ api }: { api: Api }) {
  const [servers, setServers] = useState<ServerView[]>();
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    try {
      setServers((await api.get<ListMyServersResponse>('/v1/servers')).servers);
      setError('');
    } catch (err) {
      setError(errorText(err));
    }
  }, [api]);
  useRefresh(load, pollMs(servers ?? []));
  const { askWake, askSleep, dialog } = useWakeSleep(api, load, (err) => setError(errorText(err)));

  return (
    <>
      <ServersView servers={servers} error={error} now={new Date()} onWake={askWake} onSleep={askSleep} />
      {dialog}
    </>
  );
}

/** What the page shows, given what's loaded (no fetching: easy to test). */
export function ServersView(props: {
  servers: ServerView[] | undefined;
  error: string;
  now: Date;
  onWake: (server: ServerView) => void;
  onSleep: (server: ServerView) => void;
}) {
  const { servers, error, now, onWake, onSleep } = props;
  return (
    <>
      <div className="page-head page-head-action">
        <div>
          <h1 className="page-title">Servers</h1>
          {servers && servers.length > 0 && <p className="quiet">{summaryText(servers)}</p>}
        </div>
        {servers && servers.length > 0 && <a className="btn btn-primary" {...linkTo('/app/new')}>New server</a>}
      </div>
      {error && <p className="notice" role="alert">{error}</p>}
      {!servers ? (
        !error && <p className="quiet" aria-live="polite">Loading your servers…</p>
      ) : servers.length === 0 ? (
        <section className="empty">
          <span className="campsite" aria-hidden="true">
            <span className="spark" />
            <span className="spark" />
            <span className="spark" />
            <Campfire state="unlit" height={80} />
          </span>
          <h2>No servers yet</h2>
          <a className="btn btn-primary btn-tall" {...linkTo('/app/new')}>Start your first server</a>
        </section>
      ) : (
        <>
          <div className="server-grid">
            {servers.map((server) => (
              <ServerCard key={server.serverId} server={server} now={now} onWake={() => onWake(server)} onSleep={() => onSleep(server)} />
            ))}
          </div>
          <p className="quiet">Servers fall asleep when nobody's playing, so you only pay while you play.</p>
        </>
      )}
    </>
  );
}
